import React, { useState, useEffect, useRef } from 'react';
import { Search, ExternalLink, X, Loader2 } from 'lucide-react';
import { FoundPicture, searchCommons, googleImagesUrl, creditFor } from '../../studio/imageSearch';
import { pastedImageFile } from '../../utils/imageUpload';

interface ImageSearchModalProps {
  initialQuery: string;
  onCancel: () => void;
  // credit is the line to show for a Commons picture, or null for a picture the user pasted
  onPick: (image: Blob, credit: string | null) => void;
}

// A popup for finding a real photo: pick one of the free-to-use pictures found on Wikimedia Commons,
// or open Google Images in a new tab, copy a picture there, and paste it here.
const ImageSearchModal: React.FC<ImageSearchModalProps> = ({ initialQuery, onCancel, onPick }) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<FoundPicture[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Only the newest search may show its results; an older one finishing late is ignored
  const latestSearch = useRef(0);

  const runSearch = async (text: string) => {
    const searchId = ++latestSearch.current;
    setSearching(true);
    setError(null);
    try {
      const found = await searchCommons(text);
      if (searchId !== latestSearch.current) return;
      setResults(found);
    } catch (err) {
      if (searchId !== latestSearch.current) return;
      setResults(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (searchId === latestSearch.current) setSearching(false);
    }
  };

  useEffect(() => {
    runSearch(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  // Escape closes; Ctrl+V with a copied picture uses that picture
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    const onPaste = (event: ClipboardEvent) => {
      const file = event.clipboardData ? pastedImageFile(event.clipboardData.items) : null;
      if (!file) return;
      event.preventDefault();
      onPick(file, null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('paste', onPaste);
    };
  }, [onCancel, onPick]);

  const choose = async (picture: FoundPicture) => {
    setDownloading(picture.imageUrl);
    setError(null);
    try {
      const response = await fetch(picture.imageUrl);
      if (!response.ok) throw new Error(String(response.status));
      onPick(await response.blob(), creditFor(picture));
    } catch {
      setError('Could not download that picture. Try another one.');
    } finally {
      setDownloading(null);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    runSearch(query);
  };

  return (
    <div className="crop-overlay" onClick={onCancel}>
      <div className="search-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Find a picture">
        <div className="search-modal-top">
          <h3>Find a picture</h3>
          <button className="btn btn-secondary btn-small" onClick={onCancel} aria-label="Close">
            <X size={14} /> Close
          </button>
        </div>

        <form className="search-form" onSubmit={submit}>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="What to look for"
            dir="auto"
          />
          <button type="submit" className="btn btn-primary btn-small" disabled={searching || !query.trim()}>
            <Search size={14} /> Search
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-small"
            onClick={() => window.open(googleImagesUrl(query), '_blank', 'noopener')}
            disabled={!query.trim()}
          >
            <ExternalLink size={14} /> Open Google Images
          </button>
        </form>

        <p className="studio-hint">
          Click a picture below to use it. These come from Wikimedia Commons and are free to use with the credit shown.
          Or open Google Images, copy a picture there, come back to this window and press Ctrl+V.
        </p>

        {error && <div className="studio-error">{error}</div>}

        {searching ? (
          <div className="draft-busy"><Loader2 className="animate-spin" size={14} /> Searching...</div>
        ) : results && results.length === 0 ? (
          <p className="studio-hint">No pictures found on Wikimedia Commons. Try other words, or use Google Images.</p>
        ) : results && (
          <div className="search-results">
            {results.map(picture => (
              <button
                key={picture.imageUrl}
                type="button"
                className="search-result"
                onClick={() => choose(picture)}
                disabled={downloading !== null}
                title={picture.title}
              >
                <img src={picture.imageUrl} alt={picture.title} loading="lazy" />
                <span>{downloading === picture.imageUrl ? 'Downloading...' : picture.license || 'See source for license'}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageSearchModal;
